import {
  Avatar,
  Box,
  chakra,
  Divider,
  Flex,
  HStack,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Fragment, ReactNode } from 'react';

import {
  MasterClassFieldsFragment,
  MasterClassRelationPatientCasesFragment,
} from '@/@generated/codegen/API';

import CaseClusterTag from '../CaseClusterTag/CaseClusterTag';

export type MasterClassListRowType = MasterClassFieldsFragment &
  MasterClassRelationPatientCasesFragment;

type Props = {
  masterClassList: MasterClassListRowType[];
  title?: string;
  showId?: boolean;
  showCases?: boolean;
  actions?: Array<{
    render: (data: MasterClassListRowType) => ReactNode;
  }>;
};

export function MasterClassListView({ masterClassList, title, showId, showCases, actions }: Props) {
  const backgroundColor = useColorModeValue('gray.200', 'gray.700');
  const grayColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <>
      {title && (
        <Flex justify="left" mb={3}>
          <chakra.h3 fontSize="2xl" fontWeight="bold" textAlign="center">
            {title}
          </chakra.h3>
        </Flex>
      )}
      <VStack border="1px solid" borderColor="gray.200" rounded="md" spacing={0} background="white">
        {masterClassList.map((el, idx) => {
          return (
            <Fragment key={el.id}>
              <Stack
                w="full"
                direction={{ base: 'column', md: 'row' }}
                p={{ base: 4, md: 4 }}
                gap={3}
                justifyContent="space-between"
                bg="white"
                _hover={{ bg: backgroundColor }}
                position="relative"
              >
                <Box gridColumnEnd={{ base: 'span 2', md: 'unset' }}>
                  <Flex alignItems="center">
                    <Avatar name="Master Class" color="white" size="xs" />

                    <Text pl={4} pr={2}>
                      {el.title}
                    </Text>
                  </Flex>

                  <Box ml={5} pl={5}>
                    {showId && (
                      <HStack>
                        <Text>ID : </Text>
                        <chakra.p fontWeight="medium" fontSize="sm" color={grayColor}>
                          {el.id}
                        </chakra.p>
                      </HStack>
                    )}
                    {showCases && (
                      <HStack>
                        <Text>Cases : </Text>
                        <Flex>
                          {el.patientCases.edges.map((el) => {
                            return <CaseClusterTag key={el.node.id} name={el.node.legacyCaseId} />;
                          })}
                        </Flex>
                      </HStack>
                    )}
                  </Box>
                </Box>

                <Stack
                  spacing={2}
                  direction="row"
                  fontSize={{ base: 'sm' }}
                  justifySelf="flex-end"
                  alignItems="right"
                >
                  {actions &&
                    actions.map((component, index: number) => (
                      <Fragment key={index}>{component.render(el)}</Fragment>
                    ))}
                </Stack>
              </Stack>
              {masterClassList.length - 1 !== idx && <Divider m={0} />}
            </Fragment>
          );
        })}
      </VStack>
    </>
  );
}
